import { CircuitListData } from "components/inputs/circuits/CircuitList";
import { SubAppCode } from "utils/engine/consts";
import { useMemo } from "react";
import { SalesNetwork } from "types/SalesNetworks";
import * as yup from "yup";

export type EtudeFormFields = {
  departement: string | null;
  city: string | null;
  LocationInfo: GetLocationInfoResponse | null;
  systeme: string | null;
  projectType: any | null;
  installationType: "1" | "2" | null;
  antiCorrosion: "yes" | "no" | null;
  boilerForEcs: "yes" | "no" | null;
  distance_pac_ballon: number | null;
  distance_pac_batiment: number | null;
  circuits: CircuitListData | null;
  altitude: number | null;
  t_base: number | null;
  bdm_montagne_defaut: string | null;
  TcoupureQuestion: boolean;
  Tcoupure: number | null;
  application: string | null;
  subApplication: (typeof SubAppCode)[keyof typeof SubAppCode] | null;
  expertMode?: boolean;
  largeur_porte?: number;
  hauteur_porte?: number;
  NbMPJ?: number;
  NbActiv?: number;
  NbPuisages?: number;
  nbT1?: number;
  nbT2?: number;
  nbT3?: number;
  nbT4?: number;
  nbT5?: number;
  nbT6?: number;
  NbCouvM?: number;
  NbCouvS?: number;
  NbC?: number;
  NbD?: number;
  NbEm?: number;
  NbLE?: number | null;
  NbRE?: number | null;
  NbRepasMidi?: number | null;
  NbRepasSoir?: number | null;
  TpsActSport?: number;
  personnes?: number;
  PersActiv?: number;
  nbtTotal?: number;
  consoT1?: number;
  consoT2?: number;
  consoT3?: number;
  consoT4?: number;
  consoT5?: number;
  consoT6?: number;
  TEFS?: number;
  tr?: number;
  NbLit?: number;
  antiLegionellaSupport: "yes" | "no";
  Pboucle?: number | string;
  Tp?: number;
  TECS?: number;
  debut_nc?: string | null;
  fin_nc?: string | null;
  t_ete?: number | null;
  freq?: number;
  QBrosse?: number | null;
  QHp?: number | null;
  simulationOption: boolean;
  nbBallon?: any;
  Jaquette?: any;
  Ouverture?: any;
  circPrim?: number;
  circSec?: number;
  brand?: number;
  circ?: number;
  antiLegionella?: string;
  v10?: string;
  tph?: string;
  vph?: string;
  vj?: string;
  expertModeType?: string | null;
  expertModeChanged?: boolean;
  Piste?: number | null;
  period?: number | null;
  BEC?: number | null;
  Pointe?: number | null;
  etudeFormSolutionId?: number | null;
  etudeFormSolution?: EtudeSolution | null;
  Simult?: string | null;
  resetExpertMode?: boolean;
  siteName: string;
  ecs_par_repas?: number | null;
  TpsRetCons?: number | null;
  altTbaseChanged?: boolean;
  villeData?: Ville | null;
  dep_code?: string | null;
  pdfDownloadLink?: string;
  dwgDownloadLink?: string;
  inputDelay?: string;
  continueStudyHybrid?: boolean;
  calculatedSolutions?: EtudeSolution[] | null;
};

export type SubAppCodeType = (typeof SubAppCode)[keyof typeof SubAppCode];

type Zone1DataLike = {
  allData: Record<string, any>;
};

type Zone1ValidationOptions = {
  circuits?: Array<{ id?: string | null }> | null;
  requireByService: boolean;
  t: any;
  zone1Data: Zone1DataLike;
};

const OPTIONAL_CIRCUIT_FIELDS = new Set(["surpuissance"]);
const REQUIRED_BASE_FIELDS = new Set(["distance_pac_batiment", "distance_pac_ballon"]);

const buildNullableNumberSchema = () =>
  yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable();

const applyCircuitRequirement = (
  schema: yup.NumberSchema<any>,
  errorMessage: string,
  property: string,
  requireByService: boolean
) => {
  if (!requireByService || OPTIONAL_CIRCUIT_FIELDS.has(property)) {
    return schema.notRequired();
  }

  return schema.when("$service", {
    is: (value: string) =>
      value === EtudeTypes.chauffage || value === EtudeTypes.chauffageECS,
    then: (currentSchema) => currentSchema.required(errorMessage),
    otherwise: (currentSchema) => currentSchema.notRequired(),
  });
};

export const buildZone1ValidationSchema = ({
  circuits,
  requireByService,
  t,
  zone1Data,
}: Zone1ValidationOptions): yup.ObjectShape => {
  const schema: yup.ObjectShape = {};
  const circuitSchemas: any[] = [];

  for (const [key, rawValue] of Object.entries(zone1Data.allData)) {
    const value = rawValue as any;
    const errorMessage = t(value.errorMessage, [], "message");
    const match = key.match(/^circuits\[(\d+)]\.(.+)$/);

    if (match) {
      const index = parseInt(match[1], 10);
      const property = match[2];

      if (circuits?.length && index <= circuits.length) {
        if (!circuitSchemas[index]) {
          circuitSchemas[index] = {};
        }

        let fieldSchema: any = buildNullableNumberSchema().label(property);
        fieldSchema = applyCircuitRequirement(
          fieldSchema,
          errorMessage,
          property,
          requireByService
        );

        if (value.minimum !== undefined) {
          fieldSchema = fieldSchema.min(value.minimum, errorMessage);
        }

        if (value.maximum !== undefined) {
          fieldSchema = fieldSchema.max(value.maximum, errorMessage);
        }

        circuitSchemas[index][property] = fieldSchema;

        let typeSchema: any = buildNullableNumberSchema().typeError(
          t("ERRMESS_CIRCUIT_TYPE")
        );

        if (requireByService) {
          typeSchema = typeSchema.when("$service", {
            is: (service: string) =>
              service === EtudeTypes.chauffage || service === EtudeTypes.chauffageECS,
            then: (currentSchema: any) => currentSchema.required(t("ERRMESS_CIRCUIT_TYPE")),
            otherwise: (currentSchema: any) => currentSchema.notRequired(),
          });
        } else {
          typeSchema = typeSchema.notRequired();
        }

        circuitSchemas[index].type = typeSchema;
      }

      continue;
    }

    if (value.minimum === undefined && value.maximum === undefined) {
      continue;
    }

    let baseSchema: any = buildNullableNumberSchema();

    if (value.minimum !== undefined) {
      baseSchema = baseSchema.min(value.minimum, errorMessage);
    }

    if (value.maximum !== undefined) {
      baseSchema = baseSchema.max(value.maximum, errorMessage);
    }

    schema[key] = REQUIRED_BASE_FIELDS.has(key)
      ? baseSchema.required(errorMessage)
      : baseSchema.notRequired();
  }

  const tcoupureSchema: any =
    (schema.Tcoupure as any) ?? buildNullableNumberSchema().notRequired();

  schema.Tcoupure = tcoupureSchema.test(
    "tcoupure-greater-than-t-base",
    zone1Data.allData?.Tcoupure?.errorMessage,
    function (fieldValue: number | null | undefined, context: yup.TestContext) {
      const tBase = context.parent?.t_base;

      if (fieldValue === null || fieldValue === undefined || fieldValue === ("" as any)) {
        return true;
      }

      if (tBase === null || tBase === undefined || tBase === "") {
        return true;
      }

      return Number(fieldValue) > Number(tBase);
    }
  );

  schema.circuits = yup.array().of(
    yup.lazy((circuit: any) => {
      const circuitIndex = circuits?.findIndex((item: any) => item.id == circuit.id);
      const shape = circuitIndex !== undefined && circuitIndex >= 0 ? circuitSchemas[circuitIndex] : {};
      return yup.object().shape(shape ?? {});
    }) as any
  );

  return schema;
};

export const calcFields = [
  "nbT1",
  "nbT2",
  "nbT3",
  "nbT4",
  "nbT5",
  "nbT6",
  "nbtTotal",
  "consoT1",
  "consoT2",
  "consoT3",
  "consoT4",
  "consoT5",
  "consoT6",
  "TEFS",
  "tr",
  "NbLit",
  "consoBatiment",
  "NbCouvM",
  "NbCouvS",
  "ECSbyCouv",
  "NbC",
  "Simult",
  // "tRestau",
] as const;

export const EcsProdFields = [
  "systeme",
  "Pboucle",
  "Tp",
  "TECS",
  "antiLegionellaSupport",
  "antiLegionella",
];

export const calcFieldsSchema = {
  batiment: {
    applicatif: null,
    type: {},
  },
};

export const etape2Schema = {
  reseau_commercial: "",
  batiment: {
    applicatif: null,
    type: {},
  },
  t_eau_chaude: 0,
  temperature_primaire: 0,
  mode: "",
  systeme: "",
  pertes_thermiques_bouclage: 0,
};

export const restrictionsFields = {
  largeur_de_passage: null,
  hauteur_sous_plafond: null,
};

export const calcFieldTypes = {
  prives: {
    sous_application: null,
    nbT1: 0,
    nbT2: 0,
    nbT3: 0,
    nbT4: 0,
    nbT5: 0,
    nbT6: 0,
    consoT1: 0,
    consoT2: 0,
    consoT3: 0,
    consoT4: 0,
    consoT5: 0,
    consoT6: 0,
    consoBatiment: 0, //?
    TEFS: 0,
  },
  sociaux: {
    sous_application: null,
    nbT1: 0,
    nbT2: 0,
    nbT3: 0,
    nbT4: 0,
    nbT5: 0,
    nbT6: 0,
    consoT1: 0,
    consoT2: 0,
    consoT3: 0,
    consoT4: 0,
    consoT5: 0,
    consoT6: 0,
    consoBatiment: 0,
    TEFS: 0,
  },
  seniors: {
    sous_application: null,
    nbT1: 0,
    consoT1: 0,
    consoBatiment: 0,
    TEFS: 0,
  },
  etudiant: {
    sous_application: null,
    NbLit: 0,
    NbCouvM: 0,
    NbCouvS: 0,
    ECSbyCouv: 0,
    Cjpl: 0,
    TEFS: 0,
    tRestau: 0,
    Simult: null,
  },
  travailleur: {
    sous_application: null,
    NbLit: 0,
    NbCouvM: 0,
    NbCouvS: 0,
    ECSbyCouv: 0,
    Cjpl: 0,
    TEFS: 0,
    tRestau: 0,
    Simult: null,
  },
  camping: {
    sous_application: null,
    PfB: 0,
    NbD: 0,
    NbBV: 0,
    NbBL: 0,
  },
  affaires: {
    sous_application: null,
    NbE: 5, //?
    NbC: 0,
    NbCouvM: 0,
    NbCouvS: 0,
    Cjpc: 0,
    ECSbyCouv: 0,
    tRestau: 0,
    Simult: null, //"MIDI"
    TEFS: 0,
  },
  tourisme: {
    sous_application: null,
    NbE: 2, //?
    NbC: 0,
    NbCouvM: 0,
    NbCouvS: 0,
    Cjpc: 0,
    ECSbyCouv: 0,
    tRestau: 0,
    Simult: null,
    TEFS: 0,
  },
  montagne: {
    sous_application: null,
    NbE: 5, //?
    NbC: 0,
    NbCouvM: 0,
    NbCouvS: 0,
    Cjpc: 0,
    ECSbyCouv: 0,
    tRestau: 0,
    Simult: null,
    TEFS: 0,
  },
  football: {
    sous_application: null,
    NbD: 0,
    PersActiv: 0,
    NbMPJ: 0,
    TpsActSport: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    TEFS: 0,
  },
  rugby: {
    sous_application: null,
    NbD: 0,
    PersActiv: 0,
    NbMPJ: 0,
    TpsActSport: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    TEFS: 0,
  },
  gymnase: {
    sous_application: null,
    NbD: 0,
    personnes: 0,
    NbActiv: 0,
    TpsActSport: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    TEFS: 0,
  },
  piscine: {
    sous_application: null,
    NbD: 0,
    freq: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    NbPuisages: 0,
    TpsAffl: 0,
    Affl: 0,
    NAffl: 0,
    TEFS: 0,
  },
  multisport: {
    sous_application: null,
    NbD: 0,
    freq: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    NbPuisages: 0,
    TpsAffl: 0,
    Affl: 0,
    NAffl: 0,
    TEFS: 0,
  },
  caserne: {
    sous_application: null,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    PointeModif: 0,
    NbD: 0,
    personnes: 0,
    NbPuisages: 0,
    TpsActSport: 0,
    TEFS: 0,
  },
  internat: {
    sous_application: null,
    TEFS: 0,
    NbLit: 0,
    NbD: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    ConsoJourLit: 0,
  },
  hopital: {
    sous_application: null,
    NbLE: 0,
    NbRE: 0,
    TEFS: 0,
    VJRef: 0,
    TECS: 60,
  },
  ehpad: {
    sous_application: null,
    NbLE: 0,
    NbRE: 0,
    TEFS: 0,
    VJRef: 0,
    TECS: 60,
  },
  traditionnel: {
    sous_application: null,
    NbRepasMidi: 0,
    NbRepasSoir: 0,
    ECSbyRepas: 0,
    tRestau: 0,
    TEFS: 0,
  },
  gastronomique: {
    sous_application: null,
    NbRepasMidi: 0,
    NbRepasSoir: 0,
    ECSbyRepas: 0,
    tRestau: 0,
    TEFS: 0,
  },
  rapide: {
    sous_application: null,
    NbRepasMidi: 0,
    NbRepasSoir: 0,
    ECSbyRepas: 0,
    tRestau: 0,
    TEFS: 0,
  },
  collectif: {
    sous_application: null,
    NbRepasMidi: 0,
    NbRepasSoir: 0,
    ECSbyRepas: 0,
    tRestau: 0,
    TEFS: 0,
  },
  vestiaire: {
    sous_application: null,
    nombre_de_postes_de_douches: 0,
    personnes: 0,
    NbPuisages: 0,
    TpsActSport: 0,
    TEFS: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
  },
  station: {
    sous_application: null,
    Piste: 0,
    QBrosse: 0,
    QHp: 0,
    TEC: 0,
    TEFS: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    ConsoJourLit: 0,
  },
  indus: {
    sous_application: null,
    TEC: 0,
    BEC: 0,
    Pointe: 0,
    NbPuisages: 0,
    TpsRetCons: 0,
    TEFS: 0,
    QDch: 0,
    TpsEcDch: 0,
    TpsTotDch: 0,
    ConsoJourLit: 0,
  },
};

export const LocInstFields = [
  "t_ete",
  "distance_pac_ballon",
  "distance_pac_batiment",
  "altitude",
  "t_base",
  "hauteur_porte",
  "largeur_porte",
];
export const applicationImageMap = {
  logement:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/pan1bsh3kthfp5s.jpg",
  hotel:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/ekr1bsh3ktbxko0.jpg",
  camping:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/yix1bsh3kterzpc.jpg",
  sport:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/umg1bsh3ktowjk0.jpg",
  etablissements_scolaires:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/cut1bsh3ktl6nc0.jpg",
  caserne:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/oiz1bsh3ktq7ssg.jpg",
  sante:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/uuc1bsh3ktiox0g.jpg",
  restaurant:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/bod1bsh3ktg5pgg.jpg",
  autre_tertiaire:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/egz1bsh3ktdcls0.jpg",
  indus:
    "https://groupe-atlantic.cache.ephoto.fr/link/3c9igq/vxj1bsh3ktnm0v4.jpg",
};

export const calculatedDataOrder = {
  v10: "volume_pointe_10_min",
  tph: "duree_pointe_pluri_horaire",
  vj: "volume_journalier",
  vph: "volume_pointe_pluri_horaire",
};

export const useSubApplicationOptions = (
  t: any,
  network?: SalesNetwork,
  service?: string,
  application?: string | null
) => {
  return useMemo(() => {
    return (
      network?.services
        .find((s) => s.code === service)
        ?.applications?.find((a) => a.code === application)
        ?.subApplications?.map((sub) => ({
          text: t(sub.label),
          value: sub.code,
        })) ?? []
    );
  }, [
    application,
    network?.services.find((s) => s.code === service)?.applications,
  ]);
};

export const getServiceByCode = (
  serviceCode: string,
  network: SalesNetwork
) => {
  return network?.services.find((s) => s.code === serviceCode);
};

export const getApplicationByCode = (
  applcationCode: string,
  serviceCode: string,
  network: SalesNetwork
) => {
  return getServiceByCode(serviceCode, network)?.applications?.find(
    (a) => a.code === applcationCode
  );
};

export const getSubApplicationByCode = (
  subApplcationCode: string,
  applcationCode: string,
  serviceCode: string,
  network: SalesNetwork
) => {
  return getApplicationByCode(
    applcationCode,
    serviceCode,
    network
  )?.subApplications?.find((a) => a.code === subApplcationCode);
};

export const getSystemByCode = (
  systemeCode: string,
  subApplcationCode: string,
  applcationCode: string,
  serviceCode: string,
  network: SalesNetwork
) => {
  return getSubApplicationByCode(
    subApplcationCode,
    applcationCode,
    serviceCode,
    network
  )?.systems?.find((a) => a.code === systemeCode);
};

export const EtudeTypes = {
  chauffage: "chauffage",
  chauffageECS: "chauffage-ecs",
  ECS: "ecs",
};

export type CodeGammePac = "AHP60" | "AHP70";
export const CODE_GAMMME_PAC_VALUES = ["AHP60", "AHP70"] as CodeGammePac[];

//todo:check if needed to be removed
export const PREFERENCE_PAC_VALUES = [
  "AHP60_14",
  "AHP60_18",
  "AHP60_26",
  "AHP60_32",
  "AHP60_50",
  "AHP60_70",
  "AHP70_15",
  "AHP70_18",
  "AHP70_23",
  "AHP70_27",
  "AHP70_40",
  "AHP70_50",
  "AHP70_65",
];
